import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth } from '../services/Auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // Check if user is already signed in on app start
  useEffect(() => {
    const savedToken = localStorage.getItem('producthub-dev-portal-token');
    const savedUser = localStorage.getItem('producthub-dev-portal-user');
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        // Check if user has any employee access level (isSuperAdmin, isAdmin, or isEmployee)
        const hasEmployeeAccess = userData && (
          userData.isSuperAdmin || 
          userData.isAdmin || 
          userData.isEmployee
        );
        
        if (hasEmployeeAccess) {
          setToken(savedToken);
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          // Clear invalid data
          localStorage.removeItem('producthub-dev-portal-token');
          localStorage.removeItem('producthub-dev-portal-user');
        }
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('producthub-dev-portal-token');
        localStorage.removeItem('producthub-dev-portal-user');
      }
    }
    setIsLoading(false);
  }, []);

  const signIn = async (email, password) => {
    try {
      const auth = Auth();
      const response = await auth.login(email, password);
      
      if (response.status === 200 && response.data) {
        const { data } = response;
           
        // Check if user has any employee access level (isSuperAdmin, isAdmin, or isEmployee)
        const hasEmployeeAccess = data.user && (
          data.user.isSuperAdmin || 
          data.user.isAdmin || 
          data.user.isEmployee
        );
        
        if (hasEmployeeAccess) {
          const userToken = data.access_token; // Use correct token field
          const userData = data.user;
          
          setToken(userToken);
          setUser(userData);
          setIsAuthenticated(true);
          
          // Store in localStorage
          localStorage.setItem('producthub-dev-portal-token', userToken);
          localStorage.setItem('producthub-dev-portal-user', JSON.stringify(userData));
          
          return { success: true };
        } else {
          // Check if API provided an error message for insufficient privileges
          const apiErrorMessage = data?.message || 
                                 data?.error || 
                                 'Access denied. Employee access required.';
          return { success: false, error: apiErrorMessage };
        }
      } else {
        // Handle API error responses (401, 422, 400, etc.) - prioritize API messages
        const apiErrorMessage = response?.data?.message || 
                               response?.data?.error;
        return { success: false, error: apiErrorMessage || 'Authentication failed.' };
      }
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Extract API error message if available - prioritize API messages
      const apiErrorMessage = error?.response?.data?.message || 
                             error?.response?.data?.error;
      
      return { success: false, error: apiErrorMessage || 'Login failed. Please try again.' };
    }
  };

  const signOut = async () => {
    try {
      if (token) {
        const auth = Auth();
        await auth.logout(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state and localStorage regardless of API response
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      localStorage.removeItem('producthub-dev-portal-token');
      localStorage.removeItem('producthub-dev-portal-user');
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    token,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 